import SectionSeparator from 'pages/client/Orders/Make/components/SectionSeparator';
import { previewGroupGridClsx } from 'pages/client/Orders/Make/classnames';
import FileRoundedIcon from 'assets/svg/FileRounded';
import { OrderUnitsType } from 'utils/enums';
import { priceFormatter, unitsFormatter } from 'utils/formatters';
import { useDataValues } from 'providers/DataValues';
import FieldBlock from 'components/preview/FieldBlock';
import { ParsedOrder } from 'api/order.parsers';

type PreviewProps = ParsedOrder & {
  currency?: string;
};

/**
 * Renders general information fields for a trade detail view.
 * Accepts an optional `currency` prop to display the correct currency code
 * in price labels; falls back to 'PHP' when not provided.
 */
export default function MakeOrderPreview(props: PreviewProps) {
  const { appValues, loading } = useDataValues();

  // fall back to PHP when currency is not available
  const currencyCode = props.currency || 'PHP';

  const customAttrs = props.customAttrs.filter((customField) => !customField.code.endsWith('-any'));

  return (
    <>
      <SectionSeparator Icon={FileRoundedIcon}>General Information</SectionSeparator>

      <div className={'h-5 md:h-8'} />

      <section className={'flex flex-col gap-4'}>
        <div className={previewGroupGridClsx}>
          <FieldBlock label={'Category'}>{props.category?.label}</FieldBlock>
          <FieldBlock label={'Product'}>{props.product?.label}</FieldBlock>
          <FieldBlock label={'Variety'}>{props.variety?.label}</FieldBlock>
          <FieldBlock label={'Type'}>{props.unitType === OrderUnitsType.Weight ? 'Weight' : 'Pieces'}</FieldBlock>
        </div>

        {!!customAttrs.length && (
          <div className={previewGroupGridClsx}>
            {props.customAttrs
              .sort((a, b) => a.type.localeCompare(b.type))
              .map((customField) => (
                <FieldBlock
                  key={customField.id}
                  label={appValues?.attrTypes.find((at) => at.code === customField.type)?.label || ''}
                  loading={loading}
                >
                  {customField.label}
                </FieldBlock>
              ))}
          </div>
        )}

        <div className={previewGroupGridClsx}>
          <FieldBlock label={props.unitType === OrderUnitsType.Weight ? 'Weight (kg)' : 'Number of Pieces'}>
            {unitsFormatter.format(props.unitAmount)}
          </FieldBlock>

          {!!props.unitMinimumAmount && (
            <FieldBlock label={props.unitType === OrderUnitsType.Weight ? 'Minimum Weight (kg)' : 'Minimum Amount'}>
              {unitsFormatter.format(props.unitMinimumAmount)}
            </FieldBlock>
          )}

          <FieldBlock
            label={
              props.unitType === OrderUnitsType.Weight ? `Price/kg (${currencyCode})` : `Price/piece (${currencyCode})`
            }
          >
            {priceFormatter.format(props.unitPrice)}
          </FieldBlock>

          <FieldBlock label={`Total price (${currencyCode})`}>
            {priceFormatter.format(props.unitAmount * props.unitPrice)}
          </FieldBlock>
        </div>
      </section>
    </>
  );
}
